import type { Campana, CanalCampana, ContenidoCampana, Segmento } from '../../api/ops/campanas';
import { aCentimos, errorDeImporte } from '../../logica/dinero';

/**
 * Lógica sin estilos del editor de campañas (12.2): el borrador que se edita, lo que se manda a la API (`content` y
 * `segment`), los errores de cada paso y las variables de una plantilla de WhatsApp (`{{1}}`…, G745).
 */
export type Variable = 'customer_first_name' | 'business_name' | 'booking_link';
export type UltimaVisita = '' | 'in30' | 'gt60' | 'gt90' | 'gt180';

export interface Borrador {
    nombre: string;
    canal: CanalCampana;
    plantilla: string;
    variables: Record<string, Variable>;
    texto: string;
    asunto: string;
    cuerpo: string;
    etiquetas: string[];
    modoEtiquetas: 'any' | 'all';
    ultima: UltimaVisita;
    visitas: string;
    /** «Vino a» (revisión 12.6): servicios de Citas y eventos (`service_ids_any`, `event_ids_any`); en el editor, uno de cada (G940, G941). */
    servicios: string[];
    eventos: string[];
    /** Gasto mínimo en total, como se teclea («50», «12,50»), en la moneda del negocio (`total_spent_min`). */
    gasto: string;
    moneda: string;
    edad: boolean;
    cuando: 'ahora' | 'luego';
    fecha: string;
    hora: string;
}

/** Horas de media en media hora (como el diseño). */
export const HORAS: readonly string[] = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 === 1 ? '30' : '00'}`);

const DIA_MS = 86_400_000;
const DIAS: Readonly<Record<Exclude<UltimaVisita, ''>, number>> = { in30: 30, gt60: 60, gt90: 90, gt180: 180 };

const ORDEN: readonly Variable[] = ['customer_first_name', 'business_name', 'booking_link'];

/** Las variables `{{n}}` del cuerpo de la plantilla, cada una con su dato por defecto (nombre, negocio, enlace). */
export function variablesDe(cuerpo: string): Record<string, Variable> {
    const claves = [...new Set([...cuerpo.matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((m) => m[1] ?? ''))].filter((k) => k !== '').sort((a, b) => Number(a) - Number(b));
    return Object.fromEntries(claves.map((k, i) => [k, ORDEN[i] ?? 'customer_first_name']));
}

/** El borrador de una campaña (o uno vacío para «Nueva campaña»). */
export function borradorDe(c: Campana | null, ahora: Date = new Date(), moneda = 'EUR'): Borrador {
    const s = c?.segment ?? {};
    const m = c?.content;
    const local = (c?.scheduled_local_date ?? '') !== '' ? (c?.scheduled_local_date ?? '') : '';
    return {
        nombre: c?.name ?? '',
        canal: c?.channel ?? 'whatsapp',
        plantilla: m?.meta_template_id ?? '',
        variables: { ...(m?.template_variables ?? {}) },
        texto: m?.text ?? '',
        asunto: m?.email_subject ?? '',
        cuerpo: m?.email_body_text ?? m?.email_body_html ?? '',
        etiquetas: s.tag_ids_all ?? s.tag_ids_any ?? [],
        modoEtiquetas: (s.tag_ids_all ?? []).length > 0 ? 'all' : 'any',
        ultima: ultimaDe(s, ahora),
        visitas: s.visits_min == null ? '' : String(s.visits_min),
        servicios: s.service_ids_any ?? [],
        eventos: s.event_ids_any ?? [],
        gasto: s.total_spent_min == null ? '' : importeEscrito(s.total_spent_min.amount),
        moneda: s.total_spent_min?.currency ?? moneda,
        edad: c?.age_restricted ?? false,
        cuando: c?.status === 'scheduled' ? 'luego' : 'ahora',
        fecha: local,
        hora: c?.scheduled_local_time ?? '12:00',
    };
}

/** Céntimos como se teclean en el campo: 5000 → «50», 1250 → «12,50». */
function importeEscrito(centimos: number): string {
    const euros = Math.floor(centimos / 100);
    const resto = centimos % 100;
    return resto === 0 ? String(euros) : `${String(euros)},${String(resto).padStart(2, '0')}`;
}

function ultimaDe(s: Segmento, ahora: Date): UltimaVisita {
    const dias = (iso: string) => Math.round((ahora.getTime() - new Date(iso).getTime()) / DIA_MS);
    if (s.last_visit_after != null) {
        return 'in30';
    }
    if (s.last_visit_before != null) {
        const d = dias(s.last_visit_before);
        return d >= 150 ? 'gt180' : d >= 75 ? 'gt90' : 'gt60';
    }
    return '';
}

/** El `content` que se manda; null si aún no hay nada que mandar (un borrador a medias). */
export function contenidoDe(b: Borrador): ContenidoCampana | null {
    if (b.canal === 'whatsapp') {
        return b.plantilla === '' ? null : { kind: 'meta_template', meta_template_id: b.plantilla, template_variables: b.variables };
    }
    if (b.canal === 'telegram') {
        return b.texto.trim() === '' ? null : { kind: 'text', text: b.texto };
    }
    if (b.asunto.trim() === '' && b.cuerpo.trim() === '') {
        return null;
    }
    return { kind: 'email', email_subject: b.asunto, email_body_text: b.cuerpo };
}

/** El `segment` (Clientes lo resuelve tal cual, D-078). Vacío = todos con permiso en el canal. */
export function segmentoDe(b: Borrador, ahora: Date = new Date()): Segmento {
    const s: Segmento = {};
    if (b.etiquetas.length > 0) {
        if (b.modoEtiquetas === 'all') {
            s.tag_ids_all = b.etiquetas;
        } else {
            s.tag_ids_any = b.etiquetas;
        }
    }
    if (b.ultima !== '') {
        const limite = new Date(ahora.getTime() - DIAS[b.ultima] * DIA_MS).toISOString().replace(/\.\d{3}Z$/, 'Z');
        if (b.ultima === 'in30') {
            s.last_visit_after = limite;
        } else {
            s.last_visit_before = limite;
        }
    }
    if (b.visitas !== '') {
        s.visits_min = Number(b.visitas);
    }
    if (b.servicios.length > 0) {
        s.service_ids_any = b.servicios;
    }
    if (b.eventos.length > 0) {
        s.event_ids_any = b.eventos;
    }
    const gasto = aCentimos(b.gasto);
    if (gasto !== null && gasto > 0) {
        s.total_spent_min = { amount: gasto, currency: b.moneda };
    }
    return s;
}

export interface ErroresEditor {
    nombre?: string;
    plantilla?: string;
    texto?: string;
    asunto?: string;
    cuerpo?: string;
    gasto?: string;
    fecha?: string;
}

/** Lo que falta para pasar del paso en el que se está (0 Canal, 1 Mensaje, 2 A quién, 3 Envío). */
export function erroresEditor(b: Borrador, paso: number): ErroresEditor {
    const e: ErroresEditor = {};
    if (paso === 0 && b.nombre.trim() === '') {
        e.nombre = 'Ponle un nombre: solo lo ve tu equipo.';
    }
    if (paso === 1) {
        if (b.canal === 'whatsapp' && b.plantilla === '') {
            e.plantilla = 'Elige una plantilla aprobada.';
        }
        if (b.canal === 'telegram' && b.texto.trim() === '') {
            e.texto = 'Escribe el mensaje.';
        }
        if (b.canal === 'email') {
            if (b.asunto.trim() === '') {
                e.asunto = 'Escribe el asunto.';
            }
            if (b.cuerpo.trim() === '') {
                e.cuerpo = 'Escribe el texto del email.';
            }
        }
    }
    if (paso === 2) {
        const gasto = errorDeImporte(b.gasto, false);
        if (gasto !== null) {
            e.gasto = gasto;
        }
    }
    if (paso === 3 && b.cuando === 'luego' && !/^\d{4}-\d{2}-\d{2}$/.test(b.fecha)) {
        e.fecha = 'Elige el día.';
    }
    return e;
}

/** «Para quién» de un mensaje automático (12.5): todos o con alguna etiqueta, y «Vino a» un servicio o un evento. */
export interface ParaQuien {
    modo: 'all' | 'tags';
    etiquetas: string[];
    servicio: string;
    evento: string;
}

export function paraQuienDe(s: Segmento | undefined): ParaQuien {
    const etiquetas = s?.tag_ids_any ?? [];
    return { modo: etiquetas.length > 0 ? 'tags' : 'all', etiquetas, servicio: s?.service_ids_any?.[0] ?? '', evento: s?.event_ids_any?.[0] ?? '' };
}

/**
 * El `segment` de la regla: lo que no se edita aquí (otros filtros que llegaron por la API) se conserva; etiquetas y
 * «Vino a» salen de lo elegido. Vacío = todos con permiso comercial en el canal.
 */
export function segmentoDeRegla(anterior: Segmento | undefined, p: ParaQuien): Segmento {
    const s: Segmento = { ...(anterior ?? {}) };
    delete s.tag_ids_any;
    delete s.service_ids_any;
    delete s.event_ids_any;
    if (p.modo === 'tags' && p.etiquetas.length > 0) {
        s.tag_ids_any = p.etiquetas;
    }
    if (p.servicio !== '') {
        s.service_ids_any = [p.servicio];
    }
    if (p.evento !== '') {
        s.event_ids_any = [p.evento];
    }
    return s;
}

/**
 * «Para quién» de la ficha de un mensaje automático (12.5, G944): «Con la etiqueta X o Y» con los nombres de sus etiquetas
 * (las que ya no existen no salen); sin etiquetas, todos los que tienen permiso.
 */
export function textoParaQuien(ids: readonly string[], nombres: Readonly<Record<string, string>>): string {
    if (ids.length === 0) {
        return 'Todos los que tienen permiso';
    }
    const conNombre = ids.map((id) => nombres[id]).filter((n): n is string => n !== undefined && n !== '');
    if (conNombre.length === 0) {
        return 'Con alguna de sus etiquetas';
    }
    return `Con la etiqueta ${conNombre.length === 1 ? (conNombre[0] ?? '') : `${conNombre.slice(0, -1).join(', ')} o ${conNombre[conNombre.length - 1] ?? ''}`}`;
}
